import { useState, useRef, useEffect, useContext } from 'react';
import { FiChevronDown } from 'react-icons/fi';
import { Overlay, Popover, Modal } from 'react-bootstrap';
import { FilterOption, PriceRangeItem } from 'src/types';
import { ItemInner } from './Item';
import PriceItem from './PriceItem';
import { TIME_OF_DAY_OPTIONS, TRIP_TYPE_OPTIONS } from 'src/lib/constants';
import useBreakpoints from 'src/hooks/useBreakpoints';
import { FiX } from 'react-icons/fi';
import useFilter from 'src/hooks/useFilter';
import { filterKeys, getFilterParamsFromReqQuery } from 'src/contexts/FilterProvider';
import classNames from 'classnames';
import ControlButtons from 'src/components/filter/ControlButtons';
import { FilterItemContext } from 'src/contexts/FilterItemProvider';
import { useRouter } from 'next/router';
import { useTranslations } from 'next-intl';
import { min } from 'lodash-es';

type Props = {
  durations: FilterOption[];
  childAges: FilterOption[];
  activityTypes: FilterOption[];
  suitables: FilterOption[];
  locations: FilterOption[];
  cities: FilterOption[];
  prices: [PriceRangeItem, PriceRangeItem];
  showFilter: boolean;
  notSticky?: boolean
};

const Content: React.FC<Omit<Props, 'showFilter'>> = ({
  durations,
  childAges,
  activityTypes,
  suitables,
  locations,
  cities,
  prices,
}) => {
  const { filterParams } = useFilter();
  const translation = useTranslations('components.filter.AllItem')
  const constantsTranslation = useTranslations('constants')
  const tripTypeOptions = TRIP_TYPE_OPTIONS.map((item) => ({...item, label: constantsTranslation(item.label)}))
  const timeOfDayOptions = TIME_OF_DAY_OPTIONS.map((item) => ({
    ...item,
    desc: constantsTranslation(`${item.desc}`),
    label: constantsTranslation(item.label),
  }));

  return (
    <>
      <div className="tw-pt-3">
        <div className="tw-font-styleSans tw-font-semibold tw-text-lg tw-mb-4">{translation('price')}</div>
        <PriceItem range={prices} />
      </div>
      <div className="tw-my-6 tw-border-t tw-border-grey-lighter"></div>
      <div>
        <div className="tw-font-styleSans tw-font-semibold tw-text-lg tw-mb-4">{translation('activityType')}</div>
        <ItemInner
          options={activityTypes}
          isNested
          inputType="checkbox"
          maxNumShownOption={4}
          fieldName="activityType"
          prefixFieldName="all"
          currentValue={filterParams['activityType']}
        />
      </div>
      <div className="tw-my-6 tw-border-t tw-border-grey-lighter"></div>
      <div className="">
        <div className="tw-font-styleSans tw-font-semibold tw-text-lg tw-mb-4">{translation('tripType')}</div>
        <ItemInner
          options={tripTypeOptions}
          inputType="radio"
          fieldName="tripType"
          prefixFieldName="all"
          currentValue={filterParams['tripType']}
        />
      </div>
      <div className="tw-my-6 tw-border-t tw-border-grey-lighter"></div>
      <div className="">
        <div className="tw-font-styleSans tw-font-semibold tw-text-lg tw-mb-4">{translation('timeOfDay')}</div>
        <ItemInner
          options={timeOfDayOptions}
          inputType="checkbox"
          fieldName="time"
          prefixFieldName="all"
          currentValue={filterParams['time']}
        />
      </div>
      {durations.length > 0 ? (
        <>
          <div className="tw-my-6 tw-border-t tw-border-grey-lighter"></div>
          <div className="">
            <div className="tw-font-styleSans tw-font-semibold tw-text-lg tw-mb-4">
              {translation('duration')}
            </div>
            <ItemInner
              options={durations}
              inputType="checkbox"
              fieldName="durations"
              prefixFieldName="all"
              currentValue={filterParams['durations']}
            />
          </div>
        </>
      ) : null}
      {cities.length > 1 || locations.length > 0 ? (
        <>
          <div className="tw-my-6 tw-border-t tw-border-grey-lighter"></div>
          <div className="">
            <div className="tw-font-styleSans tw-font-semibold tw-text-lg tw-mb-4">{translation('location')}</div>
            {cities.length > 1 ? (
              <ItemInner
                options={cities}
                inputType="checkbox"
                maxNumShownOption={4}
                fieldName="cityIds"
                prefixFieldName="all"
                currentValue={filterParams['cityIds']}
              />
            ) : locations.length > 0 ? (
              <ItemInner
                options={locations}
                inputType="checkbox"
                maxNumShownOption={4}
                fieldName="locationIds"
                prefixFieldName="all"
                currentValue={filterParams['locationIds']}
              />
            ) : null}
          </div>
        </>
      ) : null}
      {suitables.length > 0 ? (
        <>
          <div className="tw-my-6 tw-border-t tw-border-grey-lighter"></div>
          <div className="">
            <div className="tw-font-styleSans tw-font-semibold tw-text-lg tw-mb-4">{translation('suitableFor')}</div>
            {suitables.map((suitableGroup) => (
              <div key={suitableGroup.value}>
                <div className="tw-font-semibold tw-text-sm tw-mb-4">{suitableGroup.label}</div>
                <ItemInner
                  options={suitableGroup.children || []}
                  inputType="checkbox"
                  fieldName="suitableFor"
                  prefixFieldName="all"
                  currentValue={filterParams['suitableFor']}
                />

                {suitableGroup.value === 'family_friendly' && childAges.length > 0 ? (
                  <ItemInner
                    options={childAges}
                    inputType="checkbox"
                    fieldName="childAge"
                    prefixFieldName="all"
                    currentValue={filterParams['childAge']}
                  />
                ) : null}
              </div>
            ))}
          </div>
        </>
      ) : null}
    </>
  );
};

const Item: React.FC<Props> = ({
  durations,
  childAges,
  activityTypes,
  suitables,
  locations,
  cities,
  prices,
  showFilter,
  notSticky
}) => {
  const translation = useTranslations('components.filter.AllItem')
  const { setShowFilterItem } = useContext(FilterItemContext)
  const { filter, shouldCloseFilterItem } = useFilter();
  const { isScreenMd } = useBreakpoints();
  const itemRef = useRef<HTMLDivElement>(null);
  const [show, setShow] = useState(false);
  const fieldNames = filterKeys.filter((k) => !['cityId', 'countryId'].includes(k));

  useEffect(() => {
    if (!showFilter && show) {
      setShow(false);
    }
  }, [showFilter, show]);

  useEffect(() => {
    if (shouldCloseFilterItem) {
      setShow(false)
    }
  }, [shouldCloseFilterItem])

  useEffect(() => {
    setShowFilterItem(show)
  }, [show, setShowFilterItem])

  return (
    <>
      <div
        ref={itemRef}
        className={classNames(
          'tw-justify-center tw-flex-grow md:tw-flex-grow-0 tw-flex tw-items-center tw-leading-none tw-py-2 tw-px-3 md:tw-px-6 tw-rounded-4xl tw-border tw-cursor-pointer tw-mx-2 tw-bg-white tw-border-grey-lighter hover:tw-border-brand-navy'
        )}
        onClick={() => setShow(true)}
      >
        <span className="tw-mr-2 tw-text-sm md:tw-text-base tw-font-styleSans tw-font-semibold tw-lowercase">{translation('allFilters')}</span>
        <FiChevronDown className={classNames('tw-text-brand-navy')} />
      </div>

      {isScreenMd ? (
        <Overlay
          rootClose={true}
          target={itemRef}
          show={show}
          placement="bottom"
          onHide={() => {
            setShow(false);
          }}
        >
          <Popover
            id={`popover-filter-item-all-filters`}
            className={classNames('tw-w-72 tw-py-3 tw-px-0 popover-no-arrow tw-font-styleSans', {
              'tw-z-50': notSticky,
            })}
          >
            <div className="md:tw-max-h-sm tw-overflow-y-auto tw-relative">
              <Popover.Content className="tw-px-3">
                <Content {...{ activityTypes, suitables, locations, cities, prices, durations, childAges }} />
              </Popover.Content>
            </div>
            <ControlButtons
              setShowPopover={setShow}
              fieldNames={fieldNames}
              className="tw-pt-3 tw-border-t tw-border-grey-lighter tw-px-3"
            />
          </Popover>
        </Overlay>
      ) : (
        <Modal
          show={show}
          onHide={() => {
            setShow(false);
          }}
          className="tw-font-styleSans"
          dialogClassName="modal-dialog-bottom modal-almost-full-height--sp modal-dialog-scrollable modal-lg"
          contentClassName="tw-rounded-tl-2xl tw-rounded-tr-2xl"
        >
          <Modal.Header>
            <div className="modal-title tw-font-semibold">{translation('allFilters')}</div>
            <button type="button" className="close" onClick={() => setShow(false)}>
              <FiX strokeWidth={1} size={24} className="tw-text-brand-navy" />
            </button>
          </Modal.Header>
          <Modal.Body>
            <Content {...{ activityTypes, suitables, locations, cities, prices, durations, childAges }} />
          </Modal.Body>
          <Modal.Footer>
            <ControlButtons setShowPopover={setShow} fieldNames={fieldNames} />
          </Modal.Footer>
        </Modal>
      )}
    </>
  );
};

export default Item;
